import { useEffect, useState } from "react";
import { api, fmtDate } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { FileText, Search, Upload, FolderOpen, Download } from "lucide-react";
import { toast } from "sonner";

export default function GED() {
  const [docs, setDocs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterProject !== "all") params.set("project_id", filterProject);
    if (q) params.set("q", q);
    const [d, p] = await Promise.all([api.get(`/documents?${params.toString()}`), api.get("/projects")]);
    setDocs(d.data); setProjects(p.data); setLoading(false);
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [filterProject]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (filterProject === "all") { toast.error("Sélectionnez un projet d'abord"); return; }
    const fd = new FormData();
    fd.append("project_id", filterProject); fd.append("name", file.name); fd.append("folder", "/");
    fd.append("description", ""); fd.append("tags", ""); fd.append("file", file);
    try { await api.post("/documents/upload", fd); toast.success("Document téléversé"); fetchAll(); }
    catch { toast.error("Erreur"); }
    finally { e.target.value = ""; }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download/${d.current_version}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.name; a.click();
    } catch { toast.error("Erreur téléchargement"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-btp-terra font-semibold mb-2">Bibliothèque</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">GED — Gestion documentaire</h1>
        <p className="text-sm text-slate-500 mt-1">{docs.length} document(s) — versionning, recherche, téléchargement</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[280px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchAll()}
            placeholder="Rechercher par nom, tag…" className="w-full h-10 pl-10 pr-4 text-sm border border-slate-300 rounded-md bg-white focus:border-btp-navy focus:outline-none" data-testid="ged-search" />
        </div>
        <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white" data-testid="ged-filter-project">
          <option value="all">Tous les projets</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
        </select>
        <label className="bg-btp-terra hover:bg-btp-terra-hover text-white px-3 h-10 rounded-md text-sm font-semibold flex items-center gap-2 cursor-pointer" data-testid="ged-upload-btn">
          <Upload size={14} /> Téléverser
          <input type="file" className="hidden" onChange={upload} />
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="btp-table">
          <thead><tr><th>Document</th><th>Projet</th><th>Version</th><th>Statut</th><th>Maj</th><th>Tags</th><th>Action</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="py-8 text-center text-slate-400">Chargement…</td></tr>}
            {!loading && docs.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-slate-400"><FolderOpen className="w-10 h-10 mx-auto text-slate-300 mb-2" />Aucun document</td></tr>}
            {docs.map((d) => {
              const project = projects.find((p) => p.id === d.project_id);
              return (
                <tr key={d.id} data-testid={`ged-row-${d.id}`}>
                  <td className="font-medium">
                    <div className="flex items-center gap-2"><FileText size={14} className="text-slate-400" /> {d.name}</div>
                  </td>
                  <td className="text-xs font-mono text-slate-500">{project?.code || "—"}</td>
                  <td className="font-mono text-xs">v{d.current_version}</td>
                  <td><StatusBadge status={d.status} /></td>
                  <td className="text-xs font-mono text-slate-500">{fmtDate(d.updated_at)}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(d.tags || []).map((t, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded">{t}</span>)}
                    </div>
                  </td>
                  <td>
                    <button onClick={() => download(d)} className="text-btp-navy hover:underline text-sm flex items-center gap-1" data-testid={`download-${d.id}`}>
                      <Download size={12} /> Télécharger
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
